<template>
  <el-dialog
    :model-value="modelValue"
    :width="compact ? 'min(390px, calc(100vw - 32px))' : 'min(720px, calc(100vw - 32px))'"
    class="release-announcement-dialog"
    :class="{ 'release-announcement-compact': compact }"
    modal-class="release-announcement-overlay"
    :title="announcement?.title || '版本更新'"
    :show-close="false"
    align-center
    append-to-body
    destroy-on-close
    @update:model-value="handleVisibilityChange"
    @opened="emit('opened')"
  >
    <template #header="{ titleId, titleClass }">
      <header class="release-header">
        <button type="button" class="release-close" aria-label="关闭更新公告" @click="dismiss">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
        </button>
        <div class="release-version-line">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="iconPaths.sparkles" /></svg>
          <span>BioIntelOS {{ displayDate }} 更新</span>
          <span class="release-version-tag">v{{ announcement?.version }}</span>
        </div>
        <h2 :id="titleId" :class="titleClass">{{ announcement?.title }}</h2>
        <p>{{ announcement?.summary }}</p>
      </header>
    </template>

    <section class="release-body" aria-label="本次更新">
      <h3>本次更新</h3>
      <div class="release-features">
        <article v-for="item in highlights" :key="item.id" class="release-feature">
          <span class="release-feature-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="iconPaths[item.iconKey] || iconPaths.sparkles" /></svg>
          </span>
          <div class="release-feature-copy">
            <div class="release-feature-heading">
              <h4>{{ item.title }}</h4>
              <span class="release-category" :class="`release-category-${item.category}`">{{ categoryLabel(item.category) }}</span>
            </div>
            <p>{{ item.description }}</p>
          </div>
        </article>
      </div>
      <p v-if="acknowledgementError" class="release-ack-error" role="alert">{{ acknowledgementError }}</p>
    </section>

    <template #footer>
      <footer class="release-footer">
        <a :href="fullLogUrl" class="release-log-link" target="_blank" rel="noopener noreferrer" @click="dismiss">
          查看完整更新日志
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 3h7v7M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></svg>
        </a>
        <button type="button" class="release-primary" :disabled="acknowledging" @click="acknowledge">{{ acknowledging ? '正在保存…' : '我知道了' }}</button>
      </footer>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed } from 'vue'
import { DEFAULT_RELEASE_LOG_URL, RELEASE_CATEGORIES, isSafeReleaseUrl } from '@/utils/releaseAnnouncements'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  announcement: { type: Object, default: null },
  compact: { type: Boolean, default: false },
  acknowledgementRequired: { type: Boolean, default: false },
  acknowledging: { type: Boolean, default: false },
  acknowledgementError: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue', 'opened', 'acknowledge'])

// 图标仅来自有限的本地映射，公告内容不能注入 SVG 或 HTML。
const iconPaths = {
  grid: 'M4 3h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm11 0h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM4 14h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1Zm11 0h5a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1Z',
  sliders: 'M3 6h4m4 0h10M3 12h10m4 0h4M3 18h4m4 0h10M7 3v6m10 0v6M7 15v6',
  database: 'M20 5c0 1.7-3.6 3-8 3S4 6.7 4 5s3.6-3 8-3 8 1.3 8 3ZM4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
  settings: 'M12 3v4m0 10v4M3 12h4m10 0h4M5.6 5.6l2.8 2.8m7.2 7.2 2.8 2.8M5.6 18.4l2.8-2.8m7.2-7.2 2.8-2.8M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Zm-3.4 0a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0Z',
  sparkles: 'm12 3 2.2 6.8L21 12l-6.8 2.2L12 21l-2.2-6.8L3 12l6.8-2.2L12 3ZM20 2v4m-2-2h4M3 18v4m-2-2h4',
  bug: 'M8 7h8v9a4 4 0 0 1-8 0V7Zm1-3 2 3m4-3-2 3M4 9h4m8 0h4M3 14h5m8 0h5M4 20l4-3m8 0 4 3M12 11v8',
  rocket: 'M9 15c2-7 6-11 12-12 0 6-4 10-11 12ZM9 8H5l-3 5h7m7 2v4l-5 3v-7M6 16l-3 5 5-3M17 7h.01',
  shield: 'M12 2 3 6v6c0 5 4 8 9 10 5-2 9-5 9-10V6L12 2Zm-5 10 3 3 7-7',
}

const highlights = computed(() => {
  const items = new Map((props.announcement?.items || []).map(item => [item.id, item]))
  return (props.announcement?.highlightIds || []).map(id => items.get(id)).filter(Boolean)
})
const displayDate = computed(() => String(props.announcement?.releaseDate || '').replace(/-/g, '.'))
const fullLogUrl = computed(() => isSafeReleaseUrl(props.announcement?.fullLogUrl)
  ? props.announcement.fullLogUrl
  : DEFAULT_RELEASE_LOG_URL)
const categoryLabel = value => RELEASE_CATEGORIES.find(category => category.value === value)?.label || '更新'

function dismiss() {
  if (!props.modelValue) return
  emit('update:modelValue', false)
}

function acknowledge() {
  if (!props.modelValue || props.acknowledging) return
  if (props.acknowledgementRequired) emit('acknowledge')
  else dismiss() // 后台只读预览没有账号已读操作。
}

function handleVisibilityChange(value) {
  // X、Esc、遮罩和父组件关闭都不代表账号已读。
  if (!value) dismiss()
}
</script>

<style>
.release-announcement-overlay { background: rgba(22, 31, 49, .5); backdrop-filter: blur(3px); }
.el-dialog.release-announcement-dialog { padding: 0; border-radius: 20px; overflow: hidden; color: #17233d; box-shadow: 0 28px 80px rgba(18, 36, 73, .34); max-height: calc(100svh - 40px); display: flex; flex-direction: column; }
.release-announcement-dialog .el-dialog__header { margin: 0; padding: 0; flex-shrink: 0; }
.release-announcement-dialog .el-dialog__body { padding: 0; min-height: 0; overflow: auto; overscroll-behavior: contain; scrollbar-width: thin; }
.release-announcement-dialog .el-dialog__footer { padding: 0; flex-shrink: 0; }
.release-announcement-dialog svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }
.release-header { position: relative; padding: 28px 30px 26px; background: radial-gradient(circle at 92% 5%, rgba(120, 82, 224, .2), transparent 38%), linear-gradient(145deg, #eff4ff, #faf7ff); }
.release-close { position: absolute; top: 19px; right: 20px; display: grid; place-items: center; width: 32px; height: 32px; padding: 6px; background: none; border: 0; border-radius: 8px; color: #69778f; cursor: pointer; }
.release-close:hover { color: #17233d; background: #17233d09; }
.release-version-line { display: flex; align-items: center; gap: 10px; color: #5c3dc1; font-size: 13px; font-weight: 600; padding-right: 30px; }
.release-version-tag { font-size: 11px; color: #765bc4; background: #7354bd0b; border: 1px solid #7354bd1c; border-radius: 5px; padding: 3px 6px; line-height: 1.1; font-weight: 500; white-space: nowrap; }
.release-header h2 { font-size: 25px; color: #17233d; line-height: 1.4; letter-spacing: -.4px; margin: 17px 0 9px; font-weight: 650; overflow-wrap: anywhere; }
.release-header p { font-size: 14px; line-height: 1.8; color: #69778f; margin: 0; }
.release-body { padding: 25px 30px 0; }
.release-body > h3 { font-size: 17px; line-height: 1.5; margin: 0 0 16px; font-weight: 600; }
.release-ack-error { margin: 16px 0 0; color: #b54708; font-size: 13px; line-height: 20px; }
.release-features { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.release-feature { display: flex; gap: 13px; border-radius: 13px; background: #f8faff; padding: 17px 16px; min-width: 0; align-items: flex-start; }
.release-feature-icon { display: grid; place-items: center; flex: 0 0 35px; width: 35px; height: 35px; background: #e7edff; color: #0840e6; border-radius: 10px; }
.release-feature-icon svg { width: 21px; height: 21px; }
.release-feature-copy { flex: 1; min-width: 0; }
.release-feature-heading { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 0 0 8px; }
.release-feature h4 { font-size: 14px; line-height: 1.45; margin: 0; font-weight: 600; }
.release-category { font-size: 11px; font-weight: 500; line-height: 1.6; color: #7c6bb0; padding: 1px 5px; border-radius: 4px; background: #eee9fa; white-space: nowrap; }
.release-category-feature { background: #e9f0ff; color: #416cbd; }
.release-feature p { font-size: 13px; line-height: 1.75; color: #69778f; margin: 0; overflow-wrap: anywhere; }
.release-footer { padding: 22px 30px 26px; display: flex; justify-content: flex-end; align-items: center; gap: 14px; background: white; }
.release-log-link { display: flex; align-items: center; gap: 6px; font-size: 13px; line-height: 20px; text-decoration: none; color: #69778f; padding: 10px 8px; border-radius: 7px; }
.release-log-link:hover { color: #0840e6; background: #f5f7ff; }
.release-log-link svg { width: 13px; height: 13px; }
.release-primary { border: 0; background: #0840e6; color: white; border-radius: 10px; font-size: 14px; font-weight: 600; padding: 12px 19px; line-height: 20px; cursor: pointer; white-space: nowrap; }
.release-primary:hover { background: #0635c7; }
.release-announcement-dialog button:focus-visible, .release-announcement-dialog a:focus-visible { outline: 2px solid #0840e6; outline-offset: 3px; }
.release-announcement-compact .release-header { padding: 25px 22px 21px; }
.release-announcement-compact .release-version-line { font-size: 11px; gap: 7px; padding-right: 20px; flex-wrap: wrap; }
.release-announcement-compact .release-header h2 { font-size: 22px; margin: 15px 0 8px; }
.release-announcement-compact .release-header p, .release-announcement-compact .release-feature p { font-size: 12px; }
.release-announcement-compact .release-close { right: 10px; top: 13px; width: 36px; height: 36px; }
.release-announcement-compact .release-body { padding: 20px 20px 0; }
.release-announcement-compact .release-features { grid-template-columns: 1fr; gap: 10px; }
.release-announcement-compact .release-feature { padding: 13px 14px; gap: 12px; }
.release-announcement-compact .release-footer { padding: 16px 20px 20px; gap: 9px; }
.release-announcement-compact .release-log-link { font-size: 12px; padding: 10px 4px; min-height: 44px; box-sizing: border-box; }
.release-announcement-compact .release-primary { font-size: 13px; padding: 12px 16px; }
@media (max-width: 560px) {
  .el-dialog.release-announcement-dialog { max-height: calc(100svh - 32px); }
  .release-header { padding: 25px 22px 21px; }
  .release-version-line { font-size: 11px; gap: 7px; padding-right: 20px; flex-wrap: wrap; }
  .release-version-tag { font-size: 10px; }
  .release-header h2 { font-size: 22px; margin: 15px 0 8px; }
  .release-header p, .release-feature p { font-size: 12px; }
  .release-close { right: 10px; top: 13px; width: 36px; height: 36px; }
  .release-body { padding: 20px 20px 0; }
  .release-body > h3 { font-size: 15px; margin-bottom: 12px; }
  .release-features { grid-template-columns: 1fr; gap: 10px; }
  .release-feature { padding: 13px 14px; gap: 12px; }
  .release-feature-heading { margin-bottom: 5px; }
  .release-footer { padding: 16px 20px 20px; gap: 9px; }
  .release-log-link { font-size: 12px; padding: 10px 4px; min-height: 44px; box-sizing: border-box; }
  .release-primary { font-size: 13px; padding: 12px 16px; }
}
</style>
